/**
 * Base Report Page - Shared template for all admin report pages
 *
 * Handles:
 * - Job submission and polling
 * - Progress display
 * - Results table
 * - Excel download
 * - Past runs dropdown
 * - URL-based caching via ?job= parameter
 * - Shared filter components with URL synchronization
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Z_TOOLBAR,
  Z_GROUP_HEADER,
  Z_COLUMN_HEADER,
} from '../../../../components/admin/stickyTable/stickyOffsets'
import { useStickyTableHead } from '../../../../components/admin/stickyTable/useStickyTableHead'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Checkbox,
  Autocomplete,
  TextField,
  Tab,
  Tabs,
} from '@mui/material'
import {
  PlayArrow as RunIcon,
  Download as DownloadIcon,
  OpenInFull as FullscreenIcon,
  Close as CloseIcon,
  TableChart as ResultsIcon,
  ManageSearch as ManageSearchIcon,
  FilterAlt as FilterAltIcon,
} from '@mui/icons-material'
import AdminPageHeader from '../../../../components/admin/shared/AdminPageHeader'
import { useReportJob } from '../../../../hooks/useReportJob'
import { platformReportsService } from '../../../../services/platformReportsService'
import { useReportFilters, ReportFilterCard, DateRangePickerField, MonthEndPickerField, ExceptionTypeMultiSelect, FilterKey, ReportFilters } from '../../../../components/shared/filters'
import { useReportBulkOperations } from '../../../../hooks/useReportBulkOperations'
import ReportBulkActionsToolbar from '../components/ReportBulkActionsToolbar'
import BulkSyncDialog from '../../../../components/shared/dialogs/BulkSyncDialog'
import { useNotify } from '../../../../components/shared/ToastProvider'
import ColumnFilterPopover from '../components/ColumnFilterPopover'
import FavouriteStar from '../FavouriteStar'
import {
  ROW_HIGHLIGHT_COLORS,
  formatValue,
  formatCellValue,
  getColumnAlignment,
  getColumnStyle,
  formatReportDate as formatDate,
} from '../../../../utils/tableFormatters'
import { getExceptionObjectType } from '../../../../config/exceptions/registry'
import { buildRowInspectorLink } from '../../../../utils/urls'
import {
  FranchiseCell,
  extractFranchiseFields,
} from '../../../../config/exceptions/shared/franchiseCell'

/**
 * Configuration for making a column's content clickable
 */
export interface ColumnLink {
  // URL source (one of these)
  field?: string              // Get URL from another field in row data
  template?: string           // URL template with {field} placeholders
  builder?: (row: Record<string, any>) => string | null | undefined  // Build URL from the row (preferred for inspector deep-links - centralised + null-safe)

  // Behavior
  external?: boolean          // Open in new tab (default: true for http URLs)
  system?: 'lightspeed' | 'xero'  // Use system popup handler
  newTab?: boolean            // For internal links: render React Router Link with target="_blank"

  // Display
  showIcon?: boolean          // Show launch icon (default: true for external)
  text?: string               // Static display text (e.g., "View") instead of cell value
}

/**
 * Configuration for error styling on cells
 * Multiple conditions can be enabled - cell shows error if ANY condition matches
 */
export interface CellError {
  empty?: boolean            // null, undefined, or string < 3 chars
  zero?: boolean             // value === 0
  thresholdPercent?: number  // abs(value) > threshold (e.g., 0.15 for ±15%)
  rowFlag?: string           // server-derived per-row boolean key (truthy = error)
}

/**
 * Custom labels for boolean display
 * Default: { true: 'Yes', false: 'No' }
 */
export interface BooleanLabels {
  true: string
  false: string
}

/**
 * Threshold-based cell coloring
 * Bands are evaluated in order — first match wins
 */
export interface ColorBand {
  min?: number    // inclusive lower bound (omit for -Infinity)
  max?: number    // inclusive upper bound (omit for +Infinity)
  bg: string      // background color
  text: string    // text color
}

export interface ColumnDef {
  key: string
  label: string
  type?: 'text' | 'currency' | 'date' | 'business_date' | 'datetime' | 'percentage' | 'integer' | 'boolean' | 'boolean_indicator' | 'sync_status'
  width?: number
  minWidth?: number
  // Hard cap on rendered column width. Applied to both header and body
  // cells via getColumnStyle + TableHeader so an auto-layout table
  // can't stretch the column past this value (e.g. the shared Issues
  // column stays exactly its chip-fit width regardless of neighbours).
  maxWidth?: number
  bold?: boolean
  align?: 'left' | 'center' | 'right'  // Column alignment (default: center, numbers: right, franchise_name: left)
  link?: ColumnLink           // Make cell content clickable
  error?: CellError           // Show error background when condition matches
  booleanLabels?: BooleanLabels  // Custom labels for boolean type (default: Yes/No)
  colorBands?: ColorBand[]    // Threshold-based cell coloring (first match wins)
  filterable?: boolean         // Enable column filter dropdown for this column
  mutedValues?: string[]       // Render these cell values in muted grey (e.g. "(Unmatched)" sentinels)
}

export interface ColumnGroup {
  label: string | null
  columnKeys: string[]
}

export interface ExceptionTypeOption {
  key: string
  label: string
  description?: string
  data_domain?: string  // Set when the report groups types by Area (see showExceptionAreaFilter)
}

export interface FilterConfig {
  showFpSelector?: boolean    // maps to 'search' filter
  showDateRange?: boolean     // maps to 'dateRange' filter (uses DateRangePickerField with presets)
  showCountry?: boolean       // maps to 'market' filter
  showFpStatus?: boolean      // maps to 'fpStatus' filter
  showRegion?: boolean        // maps to 'region' filter
  linkFpStatusToSearch?: boolean  // When true, FP Status dropdown filters the FP search dropdown results
  showExceptionTypes?: boolean    // maps to 'exceptionTypes' filter
  exceptionTypeOptions?: ExceptionTypeOption[]  // Options for exception type multi-select
  exceptionTypeSingleSelect?: boolean  // Single-select mode (one type at a time)
  showExceptionDomainFilter?: boolean  // Render a Domain dropdown beside Exception Type; filters types by data_domain
  exceptionDomainLabels?: Record<string, string>  // data_domain → display label. Keys drive the dropdown options.
  requireDateRange?: boolean     // When true, date range must be set before running report
  defaultDateRange?: 'previous_month' | 'last_30_days' | 'last_12_months'  // Pre-fill date range on first load
  dateRangeAfterFilter?: 'search' | 'fpStatus' | 'market' | 'region'  // Insert date range after this filter
  showMonthEndPicker?: boolean     // maps to month-end "As of" date picker (sets endDate only)
  defaultMonthEnd?: 'previous_month'  // Pre-fill month end date on first load
  monthEndAfterFilter?: 'search' | 'fpStatus' | 'market' | 'region'  // Insert month picker after this filter
  showMinGapDays?: boolean         // maps to 'minGapDays' filter (dropdown)
  minGapDaysOptions?: { value: string; label: string }[]  // Options for min gap days dropdown
  defaultMinGapDays?: string       // Pre-fill min gap days value on first load
  showMinVarianceAmount?: boolean  // maps to 'minVarianceAmount' filter (numeric TextField)
  defaultMinVarianceAmount?: string  // Pre-fill min variance amount value on first load (e.g. "1.00")
  showSupplierMultiSelect?: boolean  // maps to 'supplierMultiSelect' filter (searchable supplier multi-select)
  showProductFilters?: boolean       // Renders the 9-field Product Filters collapsible group
  defaultProductStatus?: 'active' | 'inactive'  // Pre-fill product status (e.g. 'active' for catalogue reports)
  scopeFilters?: { label: string; value: string }[]  // Disabled read-only inputs rendered first in the filter card; useful for showing an immutable scope like "Supplier: Pool Pro"
}

/**
 * Maps FilterConfig boolean flags to FilterKey[] for shared filter components
 */
const mapFilterConfigToKeys = (config: FilterConfig): FilterKey[] => {
  const enabled: FilterKey[] = []
  if (config.showFpSelector) enabled.push('search')
  if (config.showFpStatus) enabled.push('fpStatus')
  if (config.showCountry) enabled.push('market')
  if (config.showRegion) enabled.push('region')
  if (config.showExceptionTypes) enabled.push('exceptionTypes')
  if (config.showMinGapDays) enabled.push('minGapDays')
  if (config.showMinVarianceAmount) enabled.push('minVarianceAmount')
  if (config.showSupplierMultiSelect) enabled.push('supplierMultiSelect')
  if (config.showDateRange) enabled.push('dateRange')
  if (config.showProductFilters) {
    enabled.push('productSearch', 'productStatus', 'productType', 'mainCategory',
      'inventoryTracking', 'stock', 'lastSoldAging',
      'xeroSalesAccount', 'xeroPurchaseAccount')
  }
  return enabled
}

/**
 * Converts shared filter values (camelCase) to API parameter names (snake_case).
 * Exported for unit testing - silently dropped keys here are a common
 * drift source when new filters are added.
 */
export const convertFiltersToApiParams = (filters: ReportFilters): Record<string, any> => {
  const apiParams: Record<string, any> = {}
  if (filters.search) apiParams.fp_code = filters.search
  if (filters.startDate) apiParams.start_date = filters.startDate
  if (filters.endDate) apiParams.end_date = filters.endDate
  if (filters.market) apiParams.market = filters.market
  if (filters.fpStatus) apiParams.fp_status = filters.fpStatus
  if (filters.region) apiParams.region = filters.region
  if (filters.exceptionTypes) apiParams.exception_types = filters.exceptionTypes.split(',')
  if (filters.minGapDays) apiParams.min_gap_days = parseInt(filters.minGapDays, 10)
  if (filters.minVarianceAmount) apiParams.min_variance_amount = parseFloat(filters.minVarianceAmount)
  if (filters.productSearch) apiParams.product_search = filters.productSearch
  if (filters.productStatus) apiParams.product_status = filters.productStatus
  if (filters.productType) apiParams.product_type = filters.productType
  if (filters.mainCategory) apiParams.main_category = filters.mainCategory
  if (filters.inventoryTracking) apiParams.inventory_tracking = filters.inventoryTracking
  if (filters.stock) apiParams.stock = filters.stock
  if (filters.lastSoldAging) apiParams.last_sold_aging = filters.lastSoldAging
  if (filters.xeroSalesAccount) apiParams.xero_sales_account = filters.xeroSalesAccount
  if (filters.xeroSalesAccountCode) apiParams.xero_sales_account_code = filters.xeroSalesAccountCode
  if (filters.xeroPurchaseAccount) apiParams.xero_purchase_account = filters.xeroPurchaseAccount
  if (filters.xeroPurchaseAccountCode) apiParams.xero_purchase_account_code = filters.xeroPurchaseAccountCode
  if (filters.supplierMultiSelect) apiParams.supplier = filters.supplierMultiSelect.split(',')
  return apiParams
}

/**
 * Check if a column is the last in its group (for vertical borders)
 */
const isGroupBoundary = (key: string, groups: ColumnGroup[]): boolean => {
  return groups.some(g => g.columnKeys[g.columnKeys.length - 1] === key)
}

/**
 * Optional secondary view tab. The implicit "Detail" tab (id: 'detail')
 * always renders the standard table; extra tabs replace the table body
 * with their own ``render(job)`` output. ``job.result_summary`` carries
 * any non-primitive data (e.g. ``fp_summary`` row arrays) that tabs need.
 * Named ``id`` (not ``key``) so the column-sync test's ``key:`` regex
 * doesn't pick these up as column identifiers.
 */
export interface ResultTab {
  id: string
  label: string
  render: (job: any) => React.ReactNode
}

interface BaseReportPageProps {
  reportType: string
  title: string
  // Rendered next to the title - use for badges like Beta. Optional.
  titleSuffix?: React.ReactNode
  /**
   * Favourite-star id shown beside the title. Defaults to `reportType`
   * (operational reports get a star automatically). Pass `null` to hide the
   * star (e.g. the exceptions page when 0 or >1 types are selected).
   */
  favouriteId?: string | null
  // Names the favourite in the add tooltip (e.g. the selected exception type).
  favouriteLabel?: string
  description: string
  filterConfig?: FilterConfig
  columns: ColumnDef[]
  columnGroups?: ColumnGroup[]
  defaultSortColumn?: string
  defaultSortDirection?: 'asc' | 'desc'
  /**
   * First-render compound sort. Each key sorts by ascending or - when
   * the array is followed by direction - the report's chosen direction.
   * Default (when unset): ``['fp_name', defaultSortColumn]`` so every
   * platform report lands grouped by franchise then by its own
   * primary key. A user clicking any column header switches to that
   * single-key sort and clears the compound default.
   */
  defaultSortKeys?: string[]
  enableBulkActions?: boolean
  renderResults?: (data: Record<string, any>[], isLoading: boolean) => React.ReactNode
  extraResultTabs?: ResultTab[]
}

export default function BaseReportPage({
  reportType,
  title,
  titleSuffix,
  favouriteId,
  favouriteLabel,
  description,
  filterConfig = {},
  columns,
  columnGroups,
  defaultSortColumn = 'fp_name',
  defaultSortDirection = 'asc',
  defaultSortKeys,
  enableBulkActions = false,
  renderResults,
  extraResultTabs,
}: BaseReportPageProps) {
  // Favourite-star id: default to the report type; `null` hides the star.
  const resolvedFavouriteId = favouriteId === undefined ? reportType : favouriteId

  // Active result tab. 'detail' is the implicit default (standard table);
  // other keys come from extraResultTabs. State lives here so toggling
  // fullscreen preserves the user's tab selection.
  const [activeResultTab, setActiveResultTab] = React.useState<string>('detail')
  const { job, submit, reset, isPolling, recentJobs, selectPastJob } = useReportJob(reportType)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(50)
  const [orderBy, setOrderBy] = React.useState<string>(defaultSortColumn)
  const [order, setOrder] = React.useState<'asc' | 'desc'>(defaultSortDirection)
  // Compound default: until the user clicks a column, sort by fp_name
  // first then by the report's chosen secondary. Overridable via
  // ``defaultSortKeys``. Once the user clicks, switches to single-key
  // sort on whatever column they picked.
  const [hasUserSorted, setHasUserSorted] = React.useState(false)
  const compoundSortKeys = React.useMemo<string[]>(() => {
    if (defaultSortKeys && defaultSortKeys.length > 0) return defaultSortKeys
    const secondary = defaultSortColumn && defaultSortColumn !== 'fp_name'
      ? [defaultSortColumn]
      : []
    return ['fp_name', ...secondary]
  }, [defaultSortKeys, defaultSortColumn])
  const [fullscreenOpen, setFullscreenOpen] = React.useState(false)
  // Refs + per-row sticky `top:` for the window-pinned table header.
  // toolbarRef is attached to the Results toolbar <Box> below;
  // groupHeaderRef is attached to the optional group-header <TableRow>
  // (no-op when not rendered).
  const {
    toolbarRef,
    groupHeaderRef,
    groupHeaderTop,
    columnHeaderTop,
  } = useStickyTableHead()
  const [searchParams] = useSearchParams()
  const [selectedExceptionDomain, setSelectedExceptionDomain] = React.useState<string>(() => {
    const area = searchParams.get('area')
    const labels = filterConfig.exceptionDomainLabels
    return area && labels && labels[area] ? area : ''
  })
  const [exceptionTypeOpen, setExceptionTypeOpen] = React.useState(false)
  const [hasAutoOpenedExceptionType, setHasAutoOpenedExceptionType] = React.useState(false)

  // Domain dropdown entries are the caller-provided labels (source of truth),
  // sorted alphabetically by label. This matches the admin Exception Types
  // page, which lists every domain regardless of whether active types exist.
  const exceptionDomainEntries = useMemo<Array<[string, string]>>(() => {
    if (!filterConfig.showExceptionDomainFilter || !filterConfig.exceptionDomainLabels) return []
    return Object.entries(filterConfig.exceptionDomainLabels)
      .sort((a, b) => a[1].localeCompare(b[1]))
  }, [filterConfig.showExceptionDomainFilter, filterConfig.exceptionDomainLabels])

  // Options shown in the Exception Type dropdown — filtered by the selected Domain.
  const visibleExceptionTypeOptions = useMemo<ExceptionTypeOption[]>(() => {
    const opts = filterConfig.exceptionTypeOptions || []
    if (!filterConfig.showExceptionDomainFilter || !selectedExceptionDomain) return opts
    return opts.filter(o => o.data_domain === selectedExceptionDomain)
  }, [filterConfig.exceptionTypeOptions, filterConfig.showExceptionDomainFilter, selectedExceptionDomain])

  // Exception_code for the current report type, fetched from the
  // /admin/reports/types metadata. When set, the FP Code column is
  // deep-linked to the per-FP exception view instead of the inspector
  // landing. Null for analysis reports (no exception_code on metadata).
  const [reportExceptionCode, setReportExceptionCode] = React.useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    platformReportsService.listReportTypes().then(({ reportTypes }) => {
      if (cancelled) return
      const match = reportTypes.find(t => t.id === reportType)
      setReportExceptionCode(match?.exception_code ?? null)
    })
    return () => { cancelled = true }
  }, [reportType])

  // Inspector object_type segment for this report, if it maps to an
  // exception view (analysis reports have none).
  const reportObjectType = useMemo(
    () => (reportExceptionCode ? getExceptionObjectType(reportExceptionCode) : null),
    [reportExceptionCode],
  )

  // Per-FP deep-link the Franchise cell + fp_code column use on click,
  // tenure-pinned by the row's fp_id. Exception reports point at the per-FP
  // exception view (this report's single object_type + code, passed as the
  // override); analysis reports fall back to the inspector landing. Shared by
  // both the synthetic franchise cell and the fp_code column's link.builder,
  // via the one canonical row builder.
  const resolveFranchiseCellTo = useMemo(
    () =>
      (row: Record<string, any>): string | undefined =>
        buildRowInspectorLink(row, reportObjectType, reportExceptionCode),
    [reportObjectType, reportExceptionCode],
  )

  // Transform the FP Code column: (1) always open in a new tab so clicking
  // through an FP doesn't lose the report context; (2) when this report
  // has a registered exception_code, deep-link straight to the per-FP
  // exception view. The backend EXCEPTION_CODE constant flows here via
  // the report-types metadata so no report file repeats the string.
  // For analysis reports with no exception_code AND no caller-supplied
  // link, leave the column as plain text - resolveUrl returns null when
  // a link has neither template nor field, which would render '-'.
  const effectiveColumns = useMemo<ColumnDef[]>(() => {
    const linked = columns.map(col => {
      if (col.key !== 'fp_code') return col
      const hasCallerLink =
        col.link && (col.link.template || col.link.field || col.link.builder)
      const hasInspectorLink = !!reportObjectType
      if (!hasInspectorLink && !hasCallerLink) return col
      return {
        ...col,
        link: {
          ...col.link,
          // Centralised, tenure-pinned builder (overrides any caller template).
          ...(hasInspectorLink ? { builder: resolveFranchiseCellTo } : {}),
          external: false,
          newTab: true,
        },
      }
    })

    // Collapse the FP trio (fp_code + fp_name|franchise_name + fp_status)
    // into one synthetic ``franchise`` column rendered via FranchiseCell.
    // Render-only: exports still use the original backend COLUMNS so the
    // XLSX retains all three. Order is preserved by placing the synthetic
    // column where fp_code used to sit and dropping the other two.
    const keys = linked.map(c => c.key)
    const hasFpCode = keys.includes('fp_code')
    const nameKey = keys.includes('fp_name')
      ? 'fp_name'
      : keys.includes('franchise_name')
        ? 'franchise_name'
        : null
    const hasStatus = keys.includes('fp_status')
    if (!hasFpCode || !nameKey || !hasStatus) return linked

    const collapsed: ColumnDef[] = []
    for (const col of linked) {
      if (col.key === 'fp_code') {
        collapsed.push({
          key: 'franchise',
          label: 'Franchise',
          align: 'left',
          minWidth: 160,
        })
        continue
      }
      if (col.key === nameKey || col.key === 'fp_status') continue
      collapsed.push(col)
    }
    return collapsed
  }, [columns, reportObjectType, resolveFranchiseCellTo])

  // Column filter state
  const [columnFilters, setColumnFilters] = React.useState<Record<string, string[]>>({})
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<HTMLElement | null>(null)
  const [filterColumnKey, setFilterColumnKey] = React.useState<string | null>(null)

  // Bulk actions state
  const {
    selectedFpCodes,
    handleRowSelect,
    handleSelectAllVisible,
    clearSelection,
    isFpSelected,
    isAllSelected,
    isIndeterminate,
    selectedCount,
  } = useReportBulkOperations()
  const [bulkSyncDialogOpen, setBulkSyncDialogOpen] = useState(false)
  const { showSuccess, showError } = useNotify()

  // Convert FilterConfig to FilterKey[] for shared filter components
  const enabledFilters = useMemo(() => mapFilterConfigToKeys(filterConfig), [filterConfig])

  // Memoize the filters passed to ReportFilterCard (excluding dateRange which is rendered separately;
  // xeroSalesAccountCode and xeroPurchaseAccountCode are companion inputs rendered inside their parent state field)
  const cardFilters = useMemo(() => enabledFilters.filter(f =>
    f !== 'dateRange'
    && f !== 'exceptionTypes'
    && f !== 'minGapDays'
    && f !== 'xeroSalesAccountCode'
    && f !== 'xeroPurchaseAccountCode',
  ), [enabledFilters])

  // Use shared filter hook with URL synchronization
  // Default FP Status to Active when the filter is enabled
  const filterDefaults = useMemo(() => {
    const defaults: Record<string, string> = {}
    if (filterConfig.showFpStatus) {
      defaults.fpStatus = 'Active'
    }
    if (filterConfig.defaultDateRange === 'previous_month') {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      defaults.startDate = start.toISOString().slice(0, 10)
      defaults.endDate = end.toISOString().slice(0, 10)
    }
    if (filterConfig.defaultMonthEnd === 'previous_month') {
      const now = new Date()
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      defaults.endDate = end.toISOString().slice(0, 10)
    }
    if (filterConfig.defaultDateRange === 'last_30_days') {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 29)
      defaults.startDate = start.toISOString().slice(0, 10)
      defaults.endDate = end.toISOString().slice(0, 10)
    }
    if (filterConfig.defaultDateRange === 'last_12_months') {
      const end = new Date()
      const start = new Date(end.getFullYear() - 1, end.getMonth(), end.getDate())
      defaults.startDate = start.toISOString().slice(0, 10)
      defaults.endDate = end.toISOString().slice(0, 10)
    }
    if (filterConfig.showMinGapDays && filterConfig.defaultMinGapDays) {
      defaults.minGapDays = filterConfig.defaultMinGapDays
    }
    if (filterConfig.showMinVarianceAmount && filterConfig.defaultMinVarianceAmount) {
      defaults.minVarianceAmount = filterConfig.defaultMinVarianceAmount
    }
    if (filterConfig.showProductFilters && filterConfig.defaultProductStatus) {
      defaults.productStatus = filterConfig.defaultProductStatus
    }
    return defaults
  }, [filterConfig.showFpStatus, filterConfig.defaultDateRange, filterConfig.defaultMonthEnd, filterConfig.showMinGapDays, filterConfig.defaultMinGapDays, filterConfig.showMinVarianceAmount, filterConfig.defaultMinVarianceAmount, filterConfig.showProductFilters, filterConfig.defaultProductStatus])

  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  } = useReportFilters({
    enabledFilters,
    syncToUrl: true,
    defaults: filterDefaults,
  })

  // Derive the Domain from a URL-supplied exception type so deep links like
  // ?exceptionTypes=ex-inv-ls-avg-cost-zero pre-select both dropdowns. Runs
  // once options load (the list is fetched async in ExceptionsReport).
  useEffect(() => {
    if (!filterConfig.showExceptionDomainFilter) return
    if (selectedExceptionDomain) return
    if (!filters.exceptionTypes) return
    const opts = filterConfig.exceptionTypeOptions || []
    if (!opts.length) return
    const firstKey = filters.exceptionTypes.split(',')[0]
    const match = opts.find(o => o.key === firstKey)
    if (match?.data_domain) {
      setSelectedExceptionDomain(match.data_domain)
    }
  }, [filterConfig.showExceptionDomainFilter, filterConfig.exceptionTypeOptions, filters.exceptionTypes, selectedExceptionDomain])

  // Auto-open the Exception Type dropdown when a deep link pre-selects the
  // Domain via ?area=... but leaves the type unpicked. Only fires once per
  // mount, only when types have loaded, and never when a type is already set.
  useEffect(() => {
    if (hasAutoOpenedExceptionType) return
    if (!filterConfig.showExceptionDomainFilter) return
    if (!filterConfig.exceptionTypeSingleSelect) return
    if (!searchParams.get('area')) return
    if (!selectedExceptionDomain) return
    if (filters.exceptionTypes) return
    if (!visibleExceptionTypeOptions.length) return
    setHasAutoOpenedExceptionType(true)
    setExceptionTypeOpen(true)
  }, [
    hasAutoOpenedExceptionType,
    filterConfig.showExceptionDomainFilter,
    filterConfig.exceptionTypeSingleSelect,
    searchParams,
    selectedExceptionDomain,
    filters.exceptionTypes,
    visibleExceptionTypeOptions.length,
  ])

  // Compute group header data for rendering (resolves {dateRange} placeholder)
  const groupHeaderData = useMemo(() => {
    if (!columnGroups) return null
    return columnGroups.map(g => {
      let label = g.label
      if (label && label.includes('{dateRange}') && filters.startDate && filters.endDate) {
        const fmt = (d: string) => {
          const dt = new Date(d + 'T00:00:00')
          return `${dt.getDate()}-${dt.toLocaleDateString('en-AU', { month: 'short' })}`
        }
        label = label.replace('({dateRange})', `\n(${fmt(filters.startDate)} – ${fmt(filters.endDate)})`)
      } else if (label) {
        label = label.replace(' ({dateRange})', '')
      }
      return { label, colSpan: g.columnKeys.length }
    })
  }, [columnGroups, filters.startDate, filters.endDate])

  const isDateRangeMissing = filterConfig.requireDateRange && (!filters.startDate || !filters.endDate)

  const handleRunReport = () => {
    setPage(0)
    setColumnFilters({})
    // Clear selection when running a new report
    if (enableBulkActions) {
      clearSelection()
    }
    // Convert camelCase filter names to snake_case for API
    const apiParams = convertFiltersToApiParams(filters)
    submit(apiParams)
  }

  const handleDownload = () => {
    if (job?.job_id) {
      platformReportsService.downloadJob(job.job_id, reportType, orderBy, order)
    }
  }

  const handleReset = () => {
    reset()
    setPage(0)
    setColumnFilters({})
    // Reset re-applies the compound default sort. New report runs +
    // past-job picks deliberately keep the user's column-click sort -
    // they're inspecting the same shape with different data, so the
    // explicit sort preference travels with them.
    setHasUserSorted(false)
    setOrderBy(defaultSortColumn)
    setOrder(defaultSortDirection)
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    // User picked a column - leave the compound first-render sort
    // behind. From this point on we sort by ``orderBy`` alone.
    setHasUserSorted(true)
  }

  // Synthetic column keys that aren't directly on the row dict: map them
  // to the primary row field used for sort + first-render compound order.
  const sortFieldFor = (key: string, row: any): any => {
    if (key === 'franchise') {
      return row.fp_name ?? row.franchise_name ?? row.fp_code ?? ''
    }
    return row[key]
  }

  const getComparator = (order: 'asc' | 'desc', orderBy: string | string[]) => {
    const keys = Array.isArray(orderBy) ? orderBy : [orderBy]
    return (a: any, b: any) => {
      for (const key of keys) {
        if (!key) continue
        const aVal = sortFieldFor(key, a)
        const bVal = sortFieldFor(key, b)

        if (aVal == null && bVal == null) continue
        if (aVal == null) return order === 'asc' ? 1 : -1
        if (bVal == null) return order === 'asc' ? -1 : 1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal !== bVal) return order === 'asc' ? aVal - bVal : bVal - aVal
          continue
        }

        const comparison = String(aVal).localeCompare(String(bVal))
        if (comparison !== 0) return order === 'asc' ? comparison : -comparison
      }
      return 0
    }
  }

  const filteredData = useMemo(() => {
    if (!job?.result_data) return []
    const activeFilters = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0)
    if (activeFilters.length === 0) return job.result_data
    return job.result_data.filter((row: Record<string, any>) =>
      activeFilters.every(([key, vals]) => {
        const raw = row[key]
        return vals.includes(raw === null || raw === undefined ? '-' : String(raw))
      })
    )
  }, [job?.result_data, columnFilters])

  const displayedRows = useMemo(() => {
    const effectiveSortKeys = hasUserSorted ? orderBy : compoundSortKeys
    const sorted = [...filteredData].sort(getComparator(order, effectiveSortKeys))
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, page, rowsPerPage, order, orderBy, hasUserSorted, compoundSortKeys])

  // Build FP code to name map for bulk sync dialog
  const fpCodeToName = useMemo(() => {
    if (!job?.result_data) return {}
    const map: Record<string, string> = {}
    job.result_data.forEach((row: Record<string, any>) => {
      const fpCode = row.fp_code as string
      const name = (row.franchise_name || row.fp_name) as string
      if (fpCode && name && !map[fpCode]) {
        map[fpCode] = name
      }
    })
    return map
  }, [job?.result_data])

  // Column filter helpers
  const filterableColumns = useMemo(() => effectiveColumns.filter(c => c.filterable), [effectiveColumns])

  const uniqueColumnValues = useMemo(() => {
    if (!job?.result_data || filterableColumns.length === 0) return {}
    const result: Record<string, string[]> = {}
    filterableColumns.forEach(col => {
      const seen = new Set<string>()
      job.result_data!.forEach((row: Record<string, any>) => {
        const raw = row[col.key]
        seen.add(raw === null || raw === undefined ? '-' : String(raw))
      })
      result[col.key] = Array.from(seen)
    })
    return result
  }, [job?.result_data, filterableColumns])

  const activeFilterCount = Object.values(columnFilters).filter(v => v.length > 0).length

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, columnKey: string) => {
    event.stopPropagation()
    setFilterAnchorEl(event.currentTarget)
    setFilterColumnKey(columnKey)
  }

  const handleFilterApply = (columnKey: string, values: string[]) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: values }))
    setPage(0)
  }

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
    setFilterColumnKey(null)
  }

  const handleClearAllColumnFilters = () => {
    setColumnFilters({})
    setPage(0)
  }

  // Bulk sync handlers
  const handleBulkSyncSuccess = (message: string) => {
    showSuccess(message)
    clearSelection()
  }

  const handleBulkSyncError = (message: string) => {
    showError(message)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header, breadcrumbs, Helmet doc-title and favourite star all stay
          reactive to the dynamic `title` via AdminPageHeader. */}
      <AdminPageHeader
        pageKey="platformReportDetail"
        titleOverride={title}
        descriptionOverride={description}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Platform Reports', to: '/admin/platform-reports' },
          { label: title },
        ]}
        titleAdornment={
          <>
            {resolvedFavouriteId && (
              <FavouriteStar id={resolvedFavouriteId} label={favouriteLabel} size="medium" />
            )}
            {titleSuffix}
          </>
        }
        actions={
          recentJobs.length > 0 ? (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Recent Runs</InputLabel>
              <Select
                label="Recent Runs"
                value=""
                onChange={(e) => { selectPastJob(e.target.value as string); setColumnFilters({}); setPage(0) }}
              >
                {recentJobs.map((j) => (
                  <MenuItem key={j.job_id} value={j.job_id}>
                    {formatDate(j.created_at)} ({j.result_row_count} rows)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : undefined
        }
      />

      {/* Exception Type Selector - Standalone card above filters */}
      {filterConfig.showExceptionTypes && filterConfig.exceptionTypeOptions && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <ManageSearchIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="div">
              {filterConfig.showExceptionDomainFilter ? 'Domain & Exception Type' : `Exception Type${filterConfig.exceptionTypeSingleSelect ? '' : 's'}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {filterConfig.showExceptionDomainFilter && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="exception-domain-label">Domain</InputLabel>
                <Select
                  labelId="exception-domain-label"
                  label="Domain"
                  value={selectedExceptionDomain}
                  onChange={(e) => {
                    setSelectedExceptionDomain(e.target.value as string)
                    // Domain is required — switching always clears the type
                    // so the next pick comes from the new domain's list.
                    updateFilter('exceptionTypes', '')
                  }}
                  disabled={isPolling}
                >
                  {exceptionDomainEntries.map(([domain, label]) => (
                    <MenuItem key={domain} value={domain}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              {filterConfig.exceptionTypeSingleSelect ? (
                <Autocomplete
                  size="small"
                  open={exceptionTypeOpen}
                  onOpen={() => setExceptionTypeOpen(true)}
                  onClose={() => setExceptionTypeOpen(false)}
                  options={visibleExceptionTypeOptions}
                  getOptionLabel={(o) => o.description ? `${o.label} - ${o.description}` : o.label}
                  value={visibleExceptionTypeOptions.find(o => o.key === filters.exceptionTypes) || null}
                  onChange={(_, option) => updateFilter('exceptionTypes', option?.key || '')}
                  disabled={isPolling || (filterConfig.showExceptionDomainFilter && !selectedExceptionDomain)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        filterConfig.showExceptionDomainFilter && !selectedExceptionDomain
                          ? 'Select a Domain first'
                          : 'Search exception type...'
                      }
                      label="Exception Type"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.key}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.label}</Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  sx={{ maxWidth: 500 }}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                />
              ) : (
                <ExceptionTypeMultiSelect
                  value={filters.exceptionTypes || ''}
                  onChange={(v) => updateFilter('exceptionTypes', v)}
                  options={visibleExceptionTypeOptions}
                  disabled={isPolling || (filterConfig.showExceptionDomainFilter && !selectedExceptionDomain)}
                  label=""
                />
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Filters - Using shared ReportFilterCard component */}
      {(cardFilters.length > 0 || filterConfig.showDateRange || filterConfig.showMonthEndPicker) && (
        <ReportFilterCard
          filters={filters}
          enabledFilters={cardFilters}
          onFilterChange={updateFilter}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          loading={isPolling}
          filterLinkConfig={{
            linkFpStatusToSearch: filterConfig.linkFpStatusToSearch,
          }}
          extraFilters={
            (filterConfig.showDateRange || filterConfig.showMonthEndPicker || filterConfig.showMinGapDays || filterConfig.showMinVarianceAmount || (filterConfig.scopeFilters && filterConfig.scopeFilters.length > 0)) ? (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {filterConfig.scopeFilters?.map((s) => (
                  <Box key={s.label} sx={{ minWidth: 200 }}>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      {s.label}
                    </Typography>
                    <TextField size="small" fullWidth value={s.value} disabled />
                  </Box>
                ))}
                {filterConfig.showDateRange && (
                  <Box sx={{ minWidth: 250 }}>
                    <DateRangePickerField
                      startDate={filters.startDate || ''}
                      endDate={filters.endDate || ''}
                      onChange={(start, end) => {
                        updateFilter('startDate', start)
                        updateFilter('endDate', end)
                      }}
                      disabled={isPolling}
                    />
                  </Box>
                )}
                {!filterConfig.showDateRange && filterConfig.showMonthEndPicker && (
                  <Box sx={{ minWidth: 200 }}>
                    <MonthEndPickerField
                      endDate={filters.endDate || ''}
                      onChange={(end) => updateFilter('endDate', end)}
                      disabled={isPolling}
                    />
                  </Box>
                )}
                {filterConfig.showMinGapDays && filterConfig.minGapDaysOptions && (
                  <Box sx={{ minWidth: 220 }}>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Min Gap (Days)
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.minGapDays || ''}
                        onChange={(e) => updateFilter('minGapDays', e.target.value as string)}
                        disabled={isPolling}
                        displayEmpty
                      >
                        {filterConfig.minGapDaysOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                {filterConfig.showMinVarianceAmount && (
                  <Box sx={{ minWidth: 180 }}>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Min Variance ($)
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      value={filters.minVarianceAmount || ''}
                      onChange={(e) => updateFilter('minVarianceAmount', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="1.00"
                      disabled={isPolling}
                    />
                  </Box>
                )}
              </Box>
            ) : undefined
          }
          extraFiltersAfterFilter={filterConfig.dateRangeAfterFilter || filterConfig.monthEndAfterFilter}
          actionButton={
            <Button
              variant="contained"
              onClick={handleRunReport}
              disabled={isPolling || isDateRangeMissing}
              startIcon={isPolling ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
              title={isDateRangeMissing ? 'Please select a date range' : undefined}
            >
              {isPolling ? 'Running...' : 'Run Report'}
            </Button>
          }
        />
      )}

      {/* Progress */}
      {job && job.status === 'running' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {job.progress_message || 'Processing...'}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={job.progress_percentage || 0}
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {Math.round(job.progress_percentage || 0)}% complete
          </Typography>
        </Paper>
      )}

      {/* Error */}
      {job?.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {job.error_message || 'Report failed to execute'}
        </Alert>
      )}


      {/* Bulk Actions Toolbar - Fixed at page top (outside Paper) */}
      {enableBulkActions && (
        <ReportBulkActionsToolbar
          selectedCount={selectedCount}
          onBulkSync={() => setBulkSyncDialogOpen(true)}
          onClearSelection={clearSelection}
          variant="fixed"
        />
      )}

      {/* Results Table */}
      {job?.status === 'completed' && job.result_data && (
        // `overflow: visible` lets the toolbar AND the <thead> rows
        // (group + column header) sticky-pin to the window as the page
        // scrolls. The default branch's <TableContainer> below no longer
        // has a maxHeight - the table renders full height, and per-row
        // `position: sticky` (via useStickyTableHead) pins both header
        // rows beneath the toolbar. The renderResults branch
        // (DataViewTable platform variant) still keeps its own
        // inner scroll, so wide tables can't widen the page.
        <Paper sx={{ overflow: 'visible', borderRadius: 2 }}>
          {/* Table Header Toolbar - sticky to the window so the
            * Results / Export / Fullscreen controls stay reachable
            * while the user scrolls down through a long report.
            * `toolbarRef` lets useStickyTableHead measure this band so
            * the <thead> rows below pin flush underneath it. */}
          <Box ref={toolbarRef} sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 2,
            borderBottom: '1px solid #E6E8EA',
            backgroundColor: '#FFFFFF',
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
            position: 'sticky',
            top: 0,
            // Above both the in-house sticky <thead> rows (Z_GROUP_HEADER
            // = 8, Z_COLUMN_HEADER = 7) and the platform variant's table
            // headers (inspector Z_GROUP_HEADER = 5), so the pinned
            // toolbar always overlays scrolled-into-view rows + sticky
            // <th> cells beneath it.
            zIndex: Z_TOOLBAR,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
              <ResultsIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="h6" sx={{ mr: 1 }}>Results</Typography>
              {job.result_summary && Object.entries(job.result_summary)
                // Skip control-plane keys + any non-primitive payload
                // (e.g. fp_summary row arrays consumed by extra tabs).
                // Truncation metadata is rendered as a banner below.
                .filter(([key, value]) =>
                  key !== 'truncated'
                  && key !== 'row_limit'
                  && (typeof value === 'string' || typeof value === 'number')
                )
                .map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${typeof value === 'number' && key.toLowerCase().includes('amount')
                    ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    : value}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontSize: '0.75rem' }}
                />
              ))}
              {activeFilterCount > 0 && (
                <>
                  <Chip
                    icon={<FilterAltIcon sx={{ fontSize: 14 }} />}
                    label={`${activeFilterCount} column filter${activeFilterCount > 1 ? 's' : ''} active`}
                    size="small"
                    onDelete={handleClearAllColumnFilters}
                    sx={{ height: 24, fontSize: '0.75rem', backgroundColor: '#FFF8E1', color: '#F57F17' }}
                  />
                  <Chip
                    label={`Showing ${filteredData.length} of ${job.result_data!.length} rows`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.75rem' }}
                  />
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
                sx={{
                  borderColor: '#E6E8EA',
                  color: '#1F2A2E',
                  px: 2,
                  '&:hover': { borderColor: '#007f98', backgroundColor: '#E6F4F5' }
                }}
              >
                Export Excel
              </Button>
              <Tooltip title="Fullscreen">
                <IconButton
                  onClick={() => setFullscreenOpen(true)}
                  sx={{
                    border: '1px solid #E6E8EA',
                    borderRadius: 1,
                    '&:hover': { borderColor: '#007f98', backgroundColor: '#E6F4F5' },
                  }}
                >
                  <FullscreenIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {extraResultTabs && extraResultTabs.length > 0 && (
            <Tabs
              value={activeResultTab}
              onChange={(_, v) => setActiveResultTab(v)}
              sx={{
                px: 2,
                borderBottom: '1px solid #E6E8EA',
                minHeight: 40,
                '& .MuiTab-root': { minHeight: 40, py: 1, textTransform: 'none', fontWeight: 600 },
              }}
            >
              <Tab key="detail" value="detail" label="Detail" />
              {extraResultTabs.map(t => (
                <Tab key={t.id} value={t.id} label={t.label} />
              ))}
            </Tabs>
          )}
          {job.result_summary?.truncated && activeResultTab === 'detail' && (
            <Alert severity="warning" sx={{ mx: 2, mt: 2, mb: 0 }}>
              Results were capped at{' '}
              {Number(
                job.result_summary?.row_limit ?? job.result_row_count ?? 0,
              ).toLocaleString()}{' '}
              rows. Refine the filters (FP / market / region / exception type)
              to narrow the result set and see the full data.
            </Alert>
          )}
          {activeResultTab !== 'detail' && extraResultTabs ? (
            <Box sx={{ p: 2 }}>
              {extraResultTabs.find(t => t.id === activeResultTab)?.render(job)}
            </Box>
          ) : renderResults ? (
            renderResults(filteredData, isPolling)
          ) : (
            <></>
          )}
          {!extraResultTabs?.find(t => t.id === activeResultTab) && !renderResults && (
            <>
              {/* Window-sticky headers - page scrolls, both <thead> rows
                * pin flush below the toolbar via per-row `top:` values
                * from useStickyTableHead. `stickyHeader` is intentionally
                * omitted - MUI would apply a single shared `top:0` to
                * every <th> and fight our per-row offsets. Overflow on
                * both axes is forced to `visible`: MUI's default
                * `overflowX: 'auto'` would make TableContainer a CSS
                * scroll containing block, which traps `position: sticky`
                * inside the container instead of pinning to the window.
                * Trade-off: wide tables scroll horizontally at the body
                * level (mirrors the inspector default mode). */}
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table size="small">
                  <TableHead>
                    {groupHeaderData && (
                      <TableRow ref={groupHeaderRef}>
                        {enableBulkActions && (
                          <TableCell sx={{
                            backgroundColor: '#005F73',
                            borderBottom: '1px solid #004D5E',
                            width: 48,
                            position: 'sticky',
                            top: groupHeaderTop,
                            zIndex: Z_GROUP_HEADER,
                          }} />
                        )}
                        {groupHeaderData.map((group, i) => (
                          <TableCell
                            key={i}
                            colSpan={group.colSpan}
                            sx={{
                              backgroundColor: '#005F73',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              textAlign: 'center',
                              borderBottom: '1px solid #004D5E',
                              borderRight: '1px solid rgba(255,255,255,0.3)',
                              whiteSpace: 'pre-line',
                              py: 0.75,
                              position: 'sticky',
                              top: groupHeaderTop,
                              zIndex: Z_GROUP_HEADER,
                            }}
                          >
                            {group.label || ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                    <TableRow>
                      {enableBulkActions && (
                        <TableCell
                          padding="checkbox"
                          sx={{
                            backgroundColor: '#007f98',
                            borderBottom: '1px solid #006680',
                            width: 48,
                            position: 'sticky',
                            top: columnHeaderTop,
                            zIndex: Z_COLUMN_HEADER,
                          }}
                        >
                          <Checkbox
                            indeterminate={isIndeterminate(displayedRows)}
                            checked={isAllSelected(displayedRows)}
                            onChange={() => handleSelectAllVisible(displayedRows)}
                            sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                          />
                        </TableCell>
                      )}
                      {effectiveColumns.map((col) => {
                        const hasActiveFilter = columnFilters[col.key]?.length > 0
                        return (
                        <TableCell
                          key={col.key}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: '#007f98',
                            borderBottom: '1px solid #006680',
                            color: 'white',
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px',
                            fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
                            textAlign: getColumnAlignment(col),
                            ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                            ...(col.key === 'franchise_name' || col.key === 'fp_name' ? { width: 200, minWidth: col.minWidth || 200 } : {}),
                            ...(columnGroups && isGroupBoundary(col.key, columnGroups) ? { borderRight: '1px solid rgba(255,255,255,0.3)' } : {}),
                            position: 'sticky',
                            top: columnHeaderTop,
                            zIndex: Z_COLUMN_HEADER,
                          }}
                          sortDirection={orderBy === col.key ? order : false}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: getColumnAlignment(col) }}>
                            <TableSortLabel
                              active={orderBy === col.key}
                              direction={orderBy === col.key ? order : 'asc'}
                              onClick={() => handleRequestSort(col.key)}
                              sx={{
                                color: 'white !important',
                                '&.Mui-active': { color: 'white !important' },
                                '& .MuiTableSortLabel-icon': { color: 'white !important' },
                              }}
                            >
                              {col.label}
                            </TableSortLabel>
                            {col.filterable && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleFilterClick(e, col.key)}
                                sx={{ ml: 0.25, p: 0.25, color: hasActiveFilter ? '#FFD54F' : 'rgba(255,255,255,0.5)' }}
                              >
                                <FilterAltIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedRows.map((row, idx) => {
                      const rowHighlight = row._row_highlight as string | undefined
                      const fpCode = row.fp_code as string
                      const isSelected = enableBulkActions && isFpSelected(fpCode)
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            backgroundColor: isSelected
                              ? '#E6F4F5'
                              : rowHighlight
                                ? ROW_HIGHLIGHT_COLORS[rowHighlight]
                                : '#FFFFFF',
                            '&:hover': { backgroundColor: '#E6F4F5' },
                            '& td': { borderBottom: '1px solid #E6E8EA' },
                          }}
                        >
                          {enableBulkActions && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleRowSelect(fpCode)}
                                sx={{ color: '#007f98', '&.Mui-checked': { color: '#007f98' } }}
                              />
                            </TableCell>
                          )}
                          {effectiveColumns.map((col) => (
                            <TableCell
                              key={col.key}
                              sx={{
                                ...getColumnStyle(col, row[col.key], rowHighlight, row),
                                ...(columnGroups && isGroupBoundary(col.key, columnGroups) ? { borderRight: '1px solid #E0E0E0' } : {}),
                              }}
                            >
                              {col.key === 'franchise'
                                ? <FranchiseCell {...extractFranchiseFields(row)} to={resolveFranchiseCellTo(row)} />
                                : formatCellValue(row[col.key], col, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ borderTop: '1px solid #E6E8EA', backgroundColor: '#FAFAFA' }}>
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        fullScreen
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E6E8EA',
          py: 1.5,
        }}>
          <Typography variant="h6">{title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              sx={{
                borderColor: '#E6E8EA',
                color: '#1F2A2E',
                px: 2,
                '&:hover': { borderColor: '#007f98', backgroundColor: '#E6F4F5' }
              }}
            >
              Export Excel
            </Button>
            <IconButton onClick={() => setFullscreenOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {job?.status === 'completed' && job.result_data && (
            <>
              {/* Bulk Actions Toolbar in Fullscreen - Sticky at dialog top */}
              {enableBulkActions && (
                <ReportBulkActionsToolbar
                  selectedCount={selectedCount}
                  onBulkSync={() => setBulkSyncDialogOpen(true)}
                  onClearSelection={clearSelection}
                  variant="sticky"
                />
              )}
              {extraResultTabs && extraResultTabs.length > 0 && (
                <Tabs
                  value={activeResultTab}
                  onChange={(_, v) => setActiveResultTab(v)}
                  sx={{
                    px: 2,
                    borderBottom: '1px solid #E6E8EA',
                    minHeight: 40,
                    flexShrink: 0,
                    '& .MuiTab-root': { minHeight: 40, py: 1, textTransform: 'none', fontWeight: 600 },
                  }}
                >
                  <Tab key="detail" value="detail" label="Detail" />
                  {extraResultTabs.map(t => (
                    <Tab key={t.id} value={t.id} label={t.label} />
                  ))}
                </Tabs>
              )}
              {activeResultTab !== 'detail' && extraResultTabs ? (
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                  {extraResultTabs.find(t => t.id === activeResultTab)?.render(job)}
                </Box>
              ) : renderResults ? (
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                  {renderResults(filteredData, isPolling)}
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        {groupHeaderData && (
                          <TableRow>
                            {enableBulkActions && (
                              <TableCell sx={{ backgroundColor: '#005F73', borderBottom: '1px solid #004D5E', width: 48 }} />
                            )}
                            {groupHeaderData.map((group, i) => (
                              <TableCell
                                key={i}
                                colSpan={group.colSpan}
                                sx={{
                                  backgroundColor: '#005F73',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #004D5E',
                                  borderRight: '1px solid rgba(255,255,255,0.3)',
                                  py: 0.75,
                                }}
                              >
                                {group.label || ''}
                              </TableCell>
                            ))}
                          </TableRow>
                        )}
                        <TableRow>
                          {enableBulkActions && (
                            <TableCell
                              padding="checkbox"
                              sx={{
                                backgroundColor: '#007f98',
                                borderBottom: '1px solid #006680',
                                width: 48,
                              }}
                            >
                              <Checkbox
                                indeterminate={isIndeterminate(displayedRows)}
                                checked={isAllSelected(displayedRows)}
                                onChange={() => handleSelectAllVisible(displayedRows)}
                                sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                              />
                            </TableCell>
                          )}
                          {effectiveColumns.map((col) => {
                            const hasActiveFilter = columnFilters[col.key]?.length > 0
                            return (
                            <TableCell
                              key={col.key}
                              sx={{
                                fontWeight: 600,
                                backgroundColor: '#007f98',
                                borderBottom: '1px solid #006680',
                                color: 'white',
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                letterSpacing: '0.5px',
                                fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
                                textAlign: getColumnAlignment(col),
                                ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                                ...(col.key === 'franchise_name' || col.key === 'fp_name' ? { width: 200, minWidth: col.minWidth || 200 } : {}),
                                ...(columnGroups && isGroupBoundary(col.key, columnGroups) ? { borderRight: '1px solid rgba(255,255,255,0.3)' } : {}),
                              }}
                              sortDirection={orderBy === col.key ? order : false}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: getColumnAlignment(col) }}>
                                <TableSortLabel
                                  active={orderBy === col.key}
                                  direction={orderBy === col.key ? order : 'asc'}
                                  onClick={() => handleRequestSort(col.key)}
                                  sx={{
                                    color: 'white !important',
                                    '&.Mui-active': { color: 'white !important' },
                                    '& .MuiTableSortLabel-icon': { color: 'white !important' },
                                  }}
                                >
                                  {col.label}
                                </TableSortLabel>
                                {col.filterable && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleFilterClick(e, col.key)}
                                    sx={{ ml: 0.25, p: 0.25, color: hasActiveFilter ? '#FFD54F' : 'rgba(255,255,255,0.5)' }}
                                  >
                                    <FilterAltIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            )
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedRows.map((row, idx) => {
                          const rowHighlight = row._row_highlight as string | undefined
                          const fpCode = row.fp_code as string
                          const isSelected = enableBulkActions && isFpSelected(fpCode)
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                backgroundColor: isSelected
                                  ? '#E6F4F5'
                                  : rowHighlight
                                    ? ROW_HIGHLIGHT_COLORS[rowHighlight]
                                    : '#FFFFFF',
                                '&:hover': { backgroundColor: '#E6F4F5' },
                                '& td': { borderBottom: '1px solid #E6E8EA' },
                              }}
                            >
                              {enableBulkActions && (
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleRowSelect(fpCode)}
                                    sx={{ color: '#007f98', '&.Mui-checked': { color: '#007f98' } }}
                                  />
                                </TableCell>
                              )}
                              {effectiveColumns.map((col) => (
                                <TableCell
                                  key={col.key}
                                  sx={{
                                    ...getColumnStyle(col, row[col.key], rowHighlight, row),
                                    ...(columnGroups && isGroupBoundary(col.key, columnGroups) ? { borderRight: '1px solid #E0E0E0' } : {}),
                                  }}
                                >
                                  {col.key === 'franchise'
                                    ? <FranchiseCell {...extractFranchiseFields(row)} to={resolveFranchiseCellTo(row)} />
                                    : formatCellValue(row[col.key], col, row)}
                                </TableCell>
                              ))}
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ borderTop: '1px solid #E6E8EA', flexShrink: 0 }}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Sync Dialog */}
      {enableBulkActions && (
        <BulkSyncDialog
          open={bulkSyncDialogOpen}
          onClose={() => setBulkSyncDialogOpen(false)}
          source="report"
          selectedFpCodes={selectedFpCodes}
          getFpDisplayName={(fpCode) => fpCodeToName[fpCode] || fpCode.toUpperCase()}
          onSuccess={handleBulkSyncSuccess}
          onError={handleBulkSyncError}
        />
      )}

      {/* Column Filter Popover */}
      {filterColumnKey && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          column={effectiveColumns.find(c => c.key === filterColumnKey)!}
          allValues={uniqueColumnValues[filterColumnKey] || []}
          selectedValues={columnFilters[filterColumnKey] || []}
          formatValue={formatValue}
          onApply={handleFilterApply}
          onClose={handleFilterClose}
        />
      )}
    </Box>
  )
}
